import GeneratorBuilderView from "./generatorBuilderView";
import {demandWLGeneratorContext} from "./demandWLGeneratorContext";
import {useContext, useState, useEffect} from "react";
import { Mui, MuiIcons, apiClient, AlertTypes } from '@leap4/frontend';
import SavedTemplates from "./savedTemplates";
import ScheduleSettings from "./ScheduleSettings";
import UpdateViaIntegrationModal from "./updateViaIntegrationModal";
import RunLogsTab from "./runLogsTab";
import {demandWLGeneratorBaseUrl, xroadsMonitoringBaseUrl} from '../../../../config/generals';
import {useQuery} from "react-query";

const { Box, Button, Tab, Tabs, CircularProgress, Typography, Chip, LinearProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } = Mui;
const {
    Save: SaveIcon,
    Clear: ClearIcon,
    Schedule: ScheduleIcon,
    Autorenew: AutorenewIcon,
    Add: AddIcon,
    PlayArrow: PlayArrowIcon,
    ArrowDownward: ArrowDownwardIcon,
    Info: InfoIcon,
    CheckCircle: CheckCircleIcon,
    Error: ErrorIcon,
    Sync: SyncIcon,
} = MuiIcons;

interface IabJob {
    insertionOrder: number;
    _id: string;
    processName: string;
    status: string;
    totalRows: number;
    currentRow: number;
    msg: string | null;
    updateAt: string;
}

interface IabStatusResponse {
    /** Newest run. One run is one row, so this is the authoritative current state. */
    latest: IabJob | null;
    lastSuccess: IabJob | null;
    running: IabJob | null;
    /**
     * Set only when the NEWEST run failed — the backend already decided this, so
     * there is no success-vs-failure date comparison to do here any more.
     */
    failure: IabJob | null;
    /**
     * Dispatched, but the droplet hasn't written its `jobs` row yet (~2-3 min).
     * Previously invisible, which is why the button re-armed instantly.
     */
    pending: { job: string; dispatchedAt: string } | null;
    /** A dispatch expired without ever starting — most likely the GHA run failed. */
    dispatchTimedOut?: string | null;
    /** `running` row whose currentRow has frozen; the run is almost certainly dead. */
    runningStalled?: boolean;
    tokenExpiry?: string | null;
}

type Freshness = "fresh" | "warning" | "critical";

const formatDateAgo = (dateStr: string | null): { text: string; freshness: Freshness } => {
    if (!dateStr) return { text: "Unknown", freshness: "critical" };
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const formatted = date.toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric"
    });

    const freshness: Freshness = diffDays > 30 ? "critical" : diffDays > 7 ? "warning" : "fresh";

    if (diffDays === 0) return { text: `Today (${formatted})`, freshness };
    if (diffDays === 1) return { text: `Yesterday (${formatted})`, freshness };
    return { text: `${diffDays} days ago (${formatted})`, freshness };
};

const formatTokenExpiry = (expiryStr: string | null | undefined): { text: string; freshness: Freshness } => {
    if (!expiryStr) return { text: "Unknown", freshness: "critical" };
    const expiry = new Date(expiryStr);
    const now = new Date();
    const daysLeft = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    const formatted = expiry.toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit"
    });

    const freshness: Freshness = daysLeft < 0 ? "critical" : daysLeft < 7 ? "warning" : "fresh";

    return { text: formatted, freshness };
};

const ReportTab = ({  clearReport, setAlert}) => {

    const {
        tabIndex,
        setTabIndex,
        demandWLGenerator,
        saveOrUpdateWLGeneratorTemplate,
        setDemandWLGenerator,
        runReport,
        isLoadingReport
    } = useContext(demandWLGeneratorContext);

    const { data: iabStatus, refetch: refetchIabStatus } = useQuery<IabStatusResponse>(
        "iabDataStatus",
        async () => {
            const res = await apiClient.get(`${demandWLGeneratorBaseUrl}/iab-status`);
            return res.data;
        },
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
            // Poll while an update is in flight — pending OR running. Polling only
            // on `running` meant the ~2-3min droplet bootstrap showed nothing at
            // all: the banner kept saying "Last Updated: 8 days ago" and the
            // button stayed live, so users pressed it again. And again.
            refetchInterval: (data) => {
                const status = data as IabStatusResponse;
                return status?.running || status?.pending ? 30_000 : false;
            },
        }
    );

    const [openSchedule, setOpenSchedule] = useState<boolean>(false);
    const [triggeringIab, setTriggeringIab] = useState<boolean>(false);
    const [confirmIabOpen, setConfirmIabOpen] = useState<boolean>(false);
    const [openUpdateViaIntegration, setOpenUpdateViaIntegration] = useState<boolean>(false);
    const [isAddIntegrationDisabled, setIsAddIntegrationDisabled] = useState(
        (demandWLGenerator.integrations && demandWLGenerator.integrations.length > 0) || false
    );
    const [runningMonitor, setRunningMonitor] = useState<boolean>(false);

    // Add this useEffect to watch for changes in integrations
    useEffect(() => {
        setIsAddIntegrationDisabled(
            demandWLGenerator.integrations && demandWLGenerator.integrations.length > 0
        );
    }, [demandWLGenerator.integrations]);

    // Function to handle tab change
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleOpenSchedule = () => {
        setOpenSchedule(true);
    };

    const handleCloseSchedule = () => {
        setOpenSchedule(false);
    };

    const handleScheduleUpdate = (scheduleData: { isActive: boolean; runningDays: string[]; emails?: string[] }) => {
        setDemandWLGenerator({
            ...demandWLGenerator,
            scheduleReport: scheduleData
        });
        setOpenSchedule(false);
    };

    const handleAddIntegration = () => {
        setDemandWLGenerator({
            ...demandWLGenerator,
            integrations: ['']
        });
        setIsAddIntegrationDisabled(true);
    };

    const handleSaveTemplate = () => {
        saveOrUpdateWLGeneratorTemplate({
            id: demandWLGenerator._id,
            data: demandWLGenerator,
            ignoreValidation: false
        });
    };

    const scrollToBottom = () => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    };

    const handleRunCrossroadsMonitor = async () => {
        setRunningMonitor(true);
        try {
            const response = await apiClient.post(`${xroadsMonitoringBaseUrl}/run-now`);
            if (response.data.success) {
                setAlert?.({ 
                    msg: response.data.message || 'XRoads monitoring started successfully. Check your email for results.', 
                    type: AlertTypes.SUCCESS 
                });
            } else {
                setAlert?.({ 
                    msg: response.data.message || 'XRoads monitoring failed', 
                    type: AlertTypes.FAILURE 
                });
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error occurred';
            setAlert?.({ 
                msg: `Error running XRoads monitor: ${errorMessage}`, 
                type: AlertTypes.FAILURE 
            });
        } finally {
            setRunningMonitor(false);
        }
    };

    const handleTriggerIab = async () => {
        setTriggeringIab(true);
        try {
            await apiClient.post(`${demandWLGeneratorBaseUrl}/iab-trigger`);
            setAlert?.({
                msg: "IAB scrape triggered — it runs on an ephemeral droplet, so give it a few minutes; the banner tracks it from here.",
                type: AlertTypes.SUCCESS,
            });
            // Awaited: the server's `pending` flag is what keeps the button
            // disabled, so don't drop `triggeringIab` until we've read it back,
            // otherwise the button flickers back to enabled in between.
            await refetchIabStatus?.();
        } catch (e: any) {
            const status = e?.response?.status;
            const serverMsg = e?.response?.data?.message;
            const msg =
                status === 409 ? (serverMsg || "An IAB scrape is already running.")
                    : status === 503 ? "IAB trigger isn't configured on the server yet."
                        : "Failed to trigger IAB scrape.";
            setAlert?.({ msg, type: AlertTypes.FAILURE });
        } finally {
            setTriggeringIab(false);
        }
    };

    const lastSuccessDate = formatDateAgo(iabStatus?.lastSuccess?.updateAt || null);

    // An update is in flight when it's dispatched-but-not-started (pending) or
    // actually running. A stalled `running` row doesn't count — the scrape behind
    // it is dead, so the button must not stay disabled forever.
    const runningLive = Boolean(iabStatus?.running && !iabStatus?.runningStalled);
    const updateInFlight = Boolean(iabStatus?.pending) || runningLive;

    // Next automatic weekly run — mirrors the GHA cron "17 3 * * 5" (Fri 03:17 UTC).
    const nextWeeklyRun = (() => {
        const now = new Date();
        const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 3, 17, 0, 0));
        let add = (5 - next.getUTCDay() + 7) % 7; // 5 = Friday
        if (add === 0 && now.getTime() >= next.getTime()) add = 7; // past this Friday's slot
        next.setUTCDate(next.getUTCDate() + add);
        return next;
    })();

    const runningProgress = iabStatus?.running
        ? iabStatus.running.totalRows > 0
            ? Math.round((iabStatus.running.currentRow / iabStatus.running.totalRows) * 100)
            : null
        : null;

    return(
        <Box>
            {/* IAB Data Status Banner */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    px: 2,
                    py: 1,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: updateInFlight
                        ? "info.dark"
                        : lastSuccessDate.freshness === "critical"
                            ? "error.dark"
                            : lastSuccessDate.freshness === "warning"
                                ? "warning.dark"
                                : "action.hover",
                    opacity: (updateInFlight || lastSuccessDate.freshness !== "fresh") ? 1 : 0.85,
                }}
            >
                {/* Dispatched, waiting for the droplet to boot and claim the job */}
                {iabStatus?.pending && !iabStatus?.running && (
                    <>
                        <SyncIcon fontSize="small" sx={{ color: "info.contrastText", animation: "spin 2s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
                        <Typography variant="body2" sx={{ color: "info.contrastText" }}>
                            IAB Update Requested — starting up the droplet (usually 2-3 minutes)
                        </Typography>
                    </>
                )}

                {/* Running indicator */}
                {iabStatus?.running && (
                    <>
                        <SyncIcon fontSize="small" sx={{ color: "info.contrastText", animation: "spin 2s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
                        <Typography variant="body2" sx={{ color: "info.contrastText" }}>
                            IAB Update Running
                            {runningProgress !== null && ` — ${runningProgress}%`}
                            {iabStatus.running.totalRows > 0 && ` (${iabStatus.running.currentRow.toLocaleString()} / ${iabStatus.running.totalRows.toLocaleString()} rows)`}
                        </Typography>
                        {iabStatus?.runningStalled && (
                            <Chip label="No progress — run may have died" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />
                        )}
                        {runningProgress !== null && (
                            <LinearProgress variant="determinate" value={runningProgress} sx={{ flexBasis: "100%", height: 4, borderRadius: 2 }} />
                        )}
                    </>
                )}

                {/* Last success */}
                {!updateInFlight && !iabStatus?.running && (
                    <>
                        {lastSuccessDate.freshness === "critical"
                            ? <ErrorIcon fontSize="small" color="error" />
                            : lastSuccessDate.freshness === "warning"
                                ? <InfoIcon fontSize="small" color="warning" />
                                : <CheckCircleIcon fontSize="small" color="success" />
                        }
                        <Typography variant="body2">
                            IAB Data Last Updated: <strong>{lastSuccessDate.text}</strong>
                        </Typography>
                        {lastSuccessDate.freshness === "warning" && (
                            <Chip label="Stale" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                        )}
                        {lastSuccessDate.freshness === "critical" && (
                            <Chip label="Overdue" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />
                        )}
                    </>
                )}

                {/* Failure indicator. The backend sets `failure` only when the
                    NEWEST row failed, so a later success clears it by construction
                    — no date comparison here, which is what used to hide a
                    same-day failure behind a same-day success. */}
                {iabStatus?.failure && !updateInFlight && (
                    <>
                        <Box sx={{ mx: 1, borderLeft: 1, borderColor: "divider", height: 20 }} />
                        <ErrorIcon fontSize="small" color="error" />
                        <Typography variant="body2" color="error.main">
                            Last run failed{iabStatus.failure.msg ? `: ${iabStatus.failure.msg}` : ""}
                        </Typography>
                    </>
                )}

                {/* Dispatch that never started — the GHA run most likely aborted */}
                {iabStatus?.dispatchTimedOut && !updateInFlight && (
                    <>
                        <Box sx={{ mx: 1, borderLeft: 1, borderColor: "divider", height: 20 }} />
                        <ErrorIcon fontSize="small" color="error" />
                        <Typography variant="body2" color="error.main">
                            Last update request never started — check the GitHub Actions run
                        </Typography>
                    </>
                )}

                {/* Next automatic run (weekly cron) */}
                {!updateInFlight && !iabStatus?.running && (
                    <>
                        <Box sx={{ mx: 1, borderLeft: 1, borderColor: "divider", height: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                            Next automatic run:{" "}
                            <strong>
                                {nextWeeklyRun.toLocaleString(undefined, {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </strong>
                        </Typography>
                    </>
                )}

                {/* Right-hand group: token expiry sits directly beside the
                    trigger, so `ml: auto` belongs on this wrapper rather than on
                    the Button — otherwise the free space lands between them and
                    pushes the two apart. Wrapping keeps them together on a
                    narrow viewport too. */}
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    {!updateInFlight && !iabStatus?.running && iabStatus?.tokenExpiry && (() => {
                        const tokenExp = formatTokenExpiry(iabStatus.tokenExpiry);
                        return (
                            <>
                                <Box sx={{ mr: 1, borderLeft: 1, borderColor: "divider", height: 20 }} />
                                <Typography variant="body2" color="text.secondary">
                                    IAB Token Expires: <strong>{tokenExp.text}</strong>
                                </Typography>
                                {tokenExp.freshness === "warning" && (
                                    <Chip label="Expiring Soon" size="small" color="warning" variant="outlined" />
                                )}
                                {tokenExp.freshness === "critical" && (
                                    <Chip label="Expired" size="small" color="error" variant="outlined" />
                                )}
                            </>
                        );
                    })()}

                {/* On-demand trigger — dispatches the GHA workflow */}
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={triggeringIab ? <CircularProgress size={14} /> : <AutorenewIcon />}
                    onClick={() => setConfirmIabOpen(true)}
                    // `updateInFlight` covers the dispatched-but-not-yet-started
                    // window too, and it comes from the server — so the button is
                    // disabled for every user in every tab, surviving reloads,
                    // rather than only for the tab that pressed it.
                    disabled={triggeringIab || updateInFlight}
                    sx={{ flexShrink: 0 }}
                >
                    {iabStatus?.running && !iabStatus?.runningStalled ? "Running…"
                        : iabStatus?.pending ? "Starting…"
                            : triggeringIab ? "Triggering…"
                                : "Update IAB Data"}
                </Button>
                </Box>
            </Box>

            {/* Confirm before firing the (long) scrape */}
            <Dialog open={confirmIabOpen} onClose={() => setConfirmIabOpen(false)}>
                <DialogTitle>Trigger an IAB data update?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will take a while! <strong>appAdsTxtV1_1</strong> clocks in at a
                        few hours. It runs on a dedicated droplet, and the banner updates when
                        it starts and again when it finishes. Are you sure?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmIabOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => { setConfirmIabOpen(false); handleTriggerIab(); }}
                    >
                        Yes, run it
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Container for Tabs and Buttons */}
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Tabs value={tabIndex} onChange={handleTabChange} sx={{ flexShrink: 0 }}>
                        <Tab label="Report Builder"/>
                        <Tab label="Saved Templates"/>
                        <Tab label="Run Logs"/>
                    </Tabs>

                    {/* Buttons positioned on the right */}
                    {tabIndex === 2 && (
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={runningMonitor ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            onClick={handleRunCrossroadsMonitor}
                            disabled={runningMonitor}
                        >
                            {runningMonitor ? 'Running...' : '(DEV) Run Crossroads Monitor'}
                        </Button>
                    )}
                    {tabIndex === 0 && (
                        <Box display="flex" gap={2} flexWrap="wrap" sx={{ flexGrow: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Button
                            startIcon={<PlayArrowIcon />}
                            variant="outlined"
                            color="success"
                            onClick={() => runReport(demandWLGenerator)}
                            disabled={isLoadingReport}
                        >
                            Run Report
                        </Button>
                        <Button
                            startIcon={<ArrowDownwardIcon />}
                            variant="outlined"
                            color="primary"
                            onClick={scrollToBottom}
                        >
                            Scroll to Bottom
                        </Button>
                        <Button
                            startIcon={<ClearIcon />}
                            variant="outlined"
                            color="warning"
                            onClick={clearReport}
                        >
                            Clear Report
                        </Button>
                        <Button
                            startIcon={<SaveIcon />}
                            variant="outlined"
                            color="info"
                            onClick={handleSaveTemplate}
                        >
                            {demandWLGenerator._id ? "Update Template" : "Save Template"}
                        </Button>
                        <Button
                            startIcon={<ScheduleIcon />}
                            variant="outlined"
                            color="secondary"
                            onClick={handleOpenSchedule}
                            disabled={!demandWLGenerator._id}
                        >
                            Set Schedule
                        </Button>
                        <Button
                            startIcon={<AutorenewIcon />}
                            variant="outlined"
                            color={demandWLGenerator.updateViaIntegration ? "success" : "primary"}
                            onClick={() => {
                                if (!demandWLGenerator.updateViaIntegration) {
                                    setOpenUpdateViaIntegration(true);
                                } else {
                                    setDemandWLGenerator({
                                        ...demandWLGenerator,
                                        updateViaIntegration: false
                                    });
                                }
                            }}
                            disabled={!demandWLGenerator.integrations?.length}
                        >
                            {demandWLGenerator.updateViaIntegration ? "Auto Update On" : "Set Auto Update"}
                        </Button>
                        <Button
                            startIcon={<AddIcon />}
                            variant="outlined"
                            color="secondary"
                            onClick={handleAddIntegration}
                            disabled={isAddIntegrationDisabled}
                        >
                            Add Integrations
                        </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Render the Report Builder tab */}
            {tabIndex === 0 &&  <GeneratorBuilderView />}

            {/* Render the Saved Templates tab */}
            {tabIndex === 1 && (
                <SavedTemplates />
            )}

            {/* Render the Run Logs tab */}
            {tabIndex === 2 && (
                <RunLogsTab />
            )}

            {/* Schedule Settings Modal */}
            {openSchedule && (
                <ScheduleSettings
                    open={openSchedule}
                    onClose={handleCloseSchedule}
                    templateId={demandWLGenerator._id}
                    onSave={(_, scheduleData) => handleScheduleUpdate(scheduleData)}
                    data={demandWLGenerator.scheduleReport}
                />
            )}

            {/* Add UpdateViaIntegrationModal */}
            <UpdateViaIntegrationModal
                open={openUpdateViaIntegration}
                onClose={() => {
                    setDemandWLGenerator({
                        ...demandWLGenerator,
                        updateViaIntegration: false
                    });
                    setOpenUpdateViaIntegration(false);
                }}
                onConfirm={() => {
                    setDemandWLGenerator({
                        ...demandWLGenerator,
                        updateViaIntegration: true
                    });
                    setOpenUpdateViaIntegration(false);
                }}
            />
        </Box>
    )
}

export default ReportTab;
